import type { HeadersFunction, LoaderFunctionArgs, MetaFunction } from 'react-router'
import { useLoaderData } from 'react-router'

import social from '@/assets/social.png?url'
import PricingCalculatorIsland from '@/components/PricingCalculatorIsland'
import PricingDatabaseCallout from '@/components/PricingDatabaseCallout'
import PricingPlans, { PricingPlanDetails } from '@/components/PricingPlans'
import SiteFooter from '@/components/SiteFooter'
import SiteHeader from '@/components/SiteHeader'
import { cacheControl } from '@/http.server'
import { loadPricing } from '@/utils/pricing.server'

export async function loader(args: LoaderFunctionArgs) {
  return loadPricing(args)
}

export const headers: HeadersFunction = () => ({
  'Cache-Control': cacheControl
})

export const meta: MetaFunction<typeof loader> = ({ loaderData }) => {
  if (!loaderData) return []

  const { root } = loaderData

  const title = 'Pricing and plans — PlanetScale'
  const description = 'Discover PlanetScale database platform plans tailored for projects of all sizes.'
  const image = `${root}${social}`

  return [
    { title },
    { name: 'description', content: description },
    { property: 'og:url', content: 'https://planetscale.com/pricing' },
    { property: 'og:type', content: 'website' },
    { property: 'og:title', content: title },
    { property: 'og:image', content: image },
    { property: 'og:description', content: description },
    { property: 'twitter:card', content: 'summary_large_image' },
    { property: 'twitter:site', content: '@PlanetScale' },
    { property: 'twitter:creator', content: '@PlanetScale' },
    { property: 'twitter:url', content: 'https://planetscale.com/pricing' },
    { property: 'twitter:title', content: title },
    { property: 'twitter:description', content: description },
    { property: 'twitter:image', content: image },
    { tagName: 'link', rel: 'canonical', href: 'https://planetscale.com/pricing' }
  ]
}

export default function Pricing() {
  const loaderData = useLoaderData<typeof loader>()

  return (
    <>
      <SiteHeader />

      <main className='container mb-6 max-w-7xl px-3 sm:px-5 lg:px-12'>
        <section>
          <h1>Pricing</h1>
          <PricingDatabaseCallout />
          <div className='flex flex-col gap-y-3 lg:flex-row lg:gap-x-6'>
            <PricingCalculatorIsland data={loaderData} />
            <PricingPlans />
          </div>
          <PricingPlanDetails />
        </section>
      </main>

      <SiteFooter />
    </>
  )
}
